import { useState } from "react";
import { IconAlertCircle, IconLockFilled, IconX } from "@tabler/icons-react";
import { Alert, Button, Card, Group, Input, NumberInput, Stack, Text, Textarea, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui";
import type { CategoriaDocumentoDto } from "../services/categorias-documento-service";
import { useCrearCategoria, useEditarCategoria } from "../hooks/use-categoria-documento-mutations";

/**
 * Form de creación/edición de categoría · usado en modo "crear" cuando
 * `categoria` es undefined y en modo "editar" cuando viene la categoría.
 *
 * Las categorías de sistema bloquean el código (no se puede cambiar) ·
 * sólo permiten editar metadata visible. Esto preserva la integridad del
 * enum de dominio del backend.
 */
export function CategoriaDocumentoForm({
  categoria,
  onSalir,
}: {
  categoria?: CategoriaDocumentoDto;
  onSalir: () => void;
}) {
  const esEdicion = !!categoria;
  const esSistema = categoria?.esSistema ?? false;

  const [codigo, setCodigo] = useState(categoria?.codigo ?? "");
  const [nombre, setNombre] = useState(categoria?.nombre ?? "");
  const [descripcion, setDescripcion] = useState(categoria?.descripcion ?? "");
  const [extensiones, setExtensiones] = useState<string[]>(categoria?.extensiones ?? []);
  const [tamanoMB, setTamanoMB] = useState<number>(
    categoria ? Math.round(categoria.tamanoMaximoBytes / 1024 / 1024) : 10
  );
  const [orden, setOrden] = useState<number>(categoria?.orden ?? 99);

  const crear = useCrearCategoria({ onCreada: onSalir });
  const editar = useEditarCategoria({ onEditada: onSalir });
  const guardando = crear.isPending || editar.isPending;

  const tamanoBytes = tamanoMB * 1024 * 1024;
  const inputValido =
    nombre.trim().length > 0 && extensiones.length > 0 && tamanoMB > 0 && (esEdicion || codigo.trim().length > 0);

  const handleGuardar = () => {
    if (esEdicion) {
      editar.mutate({
        id: categoria!.id,
        input: { nombre: nombre.trim(), descripcion: descripcion.trim() || null, extensiones, tamanoMaximoBytes: tamanoBytes, orden },
      });
    } else {
      crear.mutate({ codigo: codigo.trim(), nombre: nombre.trim(), descripcion: descripcion.trim() || null, extensiones, tamanoMaximoBytes: tamanoBytes, orden });
    }
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Text fw={600}>{esEdicion ? "Editar categoría" : "Nueva categoría"}</Text>

        {esSistema ? (
          <Alert variant="readonly" icon={<IconLockFilled size={14} />}>
            Esta es una categoría de sistema · el código está atado al enum del backend y no se
            puede cambiar. Sí puedes editar el nombre visible, descripción, extensiones
            permitidas y tamaño máximo.
          </Alert>
        ) : null}

        {!esEdicion ? (
          <Alert variant="info" icon={<IconAlertCircle size={14} />}>
            Las categorías nuevas (no-sistema) no estarán disponibles en el flujo de documentos
            hasta que un desarrollador agregue el código al enum{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">CategoriaDocumento</code> del backend.
            Sirven como documentación de feature requests pendientes.
          </Alert>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Input
                  label="Código"
                  placeholder="MiCategoria"
                  value={codigo}
                  onChange={(e) => setCodigo(e.currentTarget.value)}
                  disabled={esEdicion}
                  required
                  maxLength={40}
                  description={esEdicion ? "No editable · usa el campo Nombre para cambiar el display" : "Identificador único · CamelCase"}
                />
              </span>
            </TooltipTrigger>
            {esSistema && <TooltipContent>El código identifica la categoría internamente · debe coincidir con un valor del enum</TooltipContent>}
          </Tooltip>
          <Input
            label="Nombre visible"
            placeholder="CFDI, PDF CFDI, Evidencia…"
            value={nombre}
            onChange={(e) => setNombre(e.currentTarget.value)}
            required
            maxLength={120}
            description="Lo que ve el usuario en la UI"
          />
        </div>

        <Textarea
          label="Descripción"
          placeholder="Ayuda al usuario a saber qué subir"
          value={descripcion}
          onChange={(e) => setDescripcion(e.currentTarget.value)}
          maxLength={400}
          rows={2}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ExtensionesInput value={extensiones} onChange={setExtensiones} label="Extensiones permitidas" description="Una por una · agrega el punto" />
          <NumberInput label="Tamaño máximo (MB)" value={tamanoMB} onChange={(v) => setTamanoMB(typeof v === "number" ? v : 10)} min={1} max={500} step={1} required />
          <NumberInput label="Orden" value={orden} onChange={(v) => setOrden(typeof v === "number" ? v : 99)} min={0} max={9999} step={1} description="Orden visual en la lista" />
        </div>

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onSalir} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} loading={guardando} disabled={!inputValido}>
            {esEdicion ? "Guardar cambios" : "Crear categoría"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Reemplazo puntual de `<TagsInput>` de Mantine · no hay otro call-site en
 * el repo que lo necesite, así que vive local a este form en vez de ser un
 * átomo compartido. Enter/coma agrega, Backspace en vacío quita el último.
 */
function ExtensionesInput({
  value,
  onChange,
  label,
  description,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  label: string;
  description?: string;
}) {
  const [draft, setDraft] = useState("");

  const agregar = () => {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft("");
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-foreground">
        {label}
        <span className="text-peligro-600"> *</span>
      </label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background p-1.5 focus-within:border-multiva-600 focus-within:ring-2 focus-within:ring-multiva-600/30">
        {value.map((ext) => (
          <span key={ext} className="inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-0.5 text-xs">
            {ext}
            <button type="button" onClick={() => onChange(value.filter((e) => e !== ext))} className="text-muted-foreground hover:text-foreground">
              <IconX size={10} />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              agregar();
            } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={agregar}
          placeholder=".pdf, .xml, .docx…"
          className="min-w-24 flex-1 border-none bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
    </div>
  );
}
